import { useCallback, useEffect, useRef } from "react";

import {
  markForFocusLater,
  returnFocus,
  setupScopedFocus,
  teardownScopedFocus,
} from "./helpers/focusManager";
import { focusSelector, focusable, tabbable } from "./helpers/tabbable";
import scopeTab from "./helpers/scopeTab";
import { createAriaHider } from "./helpers/ariaHider";

/**
 *  Traps focus to DOM node. Use this with a modal to ensure the user focus doesn't leave.
 * */
const useFocusTrap = (active, options = {}) => {
  const ref = useRef();
  const restoreAria = useRef(null);

  const processNode = useCallback(
    (node) => {
      // See if we should disable aria
      restoreAria.current = !options.disableAriaHider
        ? createAriaHider(node)
        : null;

      // Find the initial focus element
      let focusElement = null;
      if (options.focusSelector) {
        focusElement =
          typeof options.focusSelector === "string"
            ? node.querySelector(options.focusSelector)
            : options.focusSelector;
      }

      if (!focusElement) {
        const children = Array.from(node.querySelectorAll(focusSelector));
        focusElement =
          // Prefer tabbable elements, But fallback to any focusable element
          children.find(tabbable) ||
          // But fallback to any focusable element
          children.find(focusable) ||
          // Nothing found
          null;

        // If everything else fails, see if the node itself can handle focus
        if (!focusElement && focusable(node)) focusElement = node;
      }

      if (focusElement) {
        // Set the initial focus inside the traps
        focusElement.focus();
      }
    },
    [options.disableAriaHider, options.focusSelector]
  );

  const setRef = useCallback(
    (node) => {
      if (restoreAria.current) {
        restoreAria.current();
      }

      if (ref.current) {
        returnFocus();
        teardownScopedFocus();
      }

      if (active && node) {
        setupScopedFocus(node);
        markForFocusLater();

        // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.
        setTimeout(() => {
          if (node.ownerDocument) {
            processNode(node);
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active, processNode]
  );

  const handleKeyDown = (event) => {
    if (event.key === "Tab" && ref.current) {
      scopeTab(ref.current, event);
    }
  };

  useEffect(() => {
    if (!active) return undefined;

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [active]);

  return setRef;
};

export default useFocusTrap;
